"use client"

import { observer } from "mobx-react-lite"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface GroupedBarChartProps {
  title: string
  description?: string
  data: Array<{
    label: string
    group1: number
    group2: number
  }>
  group1Label: string
  group2Label: string
  theme?: ThemeConfigType
  className?: string
}

export const GroupedBarChart = observer(
  ({
    title,
    description,
    data,
    group1Label,
    group2Label,
    theme,
    className,
  }: GroupedBarChartProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme

    // Chart config
    const chartConfig: ChartConfig = {
      group1: {
        label: group1Label,
        color: "var(--chart-1)", // Primary color
      },
      group2: {
        label: group2Label,
        color: "var(--chart-2)", // Secondary color
      },
    }

    return (
      <Card
        className={cn(
          "w-full overflow-hidden",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader>
          <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>{title}</CardTitle>
          {description && (
            <p
              className={cn(
                "text-sm text-muted-foreground",
                !isDefaultTheme && themeClasses.typography,
              )}
            >
              {description}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={chartConfig}
            className="h-[300px] sm:h-[350px] md:h-[400px] w-full"
          >
            <BarChart data={data} accessibilityLayer>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                tick={{ fontSize: "12px" }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: "12px" }} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar dataKey="group1" fill="var(--color-group1)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="group2" fill="var(--color-group2)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    )
  },
)

GroupedBarChart.displayName = "GroupedBarChart"
